<template>
  <div class="sui-select" :class="{'sui-select--disabled': disabled}" @focusin="onFocusIn">
    <div class="sui-select__control control" :class="{ 'sui-select__control--has-errors': hasErrors}" tabindex="0">
      <div class="control__view">
        <div class="control__label-container">
          <div class="control__label" :class="{ 'control__label--focused': labelFocused }">
            <span>{{ label }}</span>
            <span v-if="required" class="required-indicator">*</span>
          </div>
        </div>
        <div class="control__options-container">
          <template v-if="!multiple">
            <div >
              {{ getLabel(modelValue as SelectOption) }}
            </div>
          </template>
          <template v-else>
            <div v-if="!chips && Array.isArray(modelValue) && modelValue.length"> {{ `${ optionsCountLabel } ${ modelValue.length || 0 }` }} </div>
            <template v-for="(value, _index) in modelValue" v-else >
              <slot name="chips" :option="(value as SelectOption)" :label="getLabel" :on-remove-chips="onRemoveChipsClick">
                <SChips 
                  :key="_index"
                  :label="getLabel(value as SelectOption)"
                  @icon-click="onRemoveChipsClick(value as SelectOption)"
                ></SChips>
              </slot>
            </template>
          </template>
          
          <div v-if="searchable" class="search search-container" tabindex="0">
            <input ref="search" :value="searchString" class="search-control" :style="{ width: searchInputSize }" @input="onSearchStringInput">
          </div>
        </div>
      </div>
      <div v-if="isClearableTriggerVisible" class="control__clear-icon">
        <Icon class="sui-icon" size="sm" name="close" @pointerdown.prevent @click.prevent="onClearClick"></Icon>
      </div>
      <div class="control__right-slot">
        <Icon v-if="!inline" class="sui-icon" size="sm" :name="dropdownIconName" @pointerdown.prevent @click="onDropdownIconClick"></Icon>
      </div>
    </div>
    <div v-if="dropDownVisibility" class="sui-select__options-wrapper" :style="dropdownStyles" @scroll="onOptionsScroll" @click.stop>
      <slot v-if="options.length > 0" name="options" :options="options">
        <div ref="optionList" class="option-list">
          <template v-if="optionsMark === 'done'">
            <div 
              v-for="(option, _index) in options"
              :key="_index"
              class="option-list__element option" 
              :class="{ 'option-list__element--selected': isOptionSelected(option) }"
              @click="onOptionClick(option)"
            >
              <slot name="option" :option="option">
                <div class="option__title"> {{ getLabel(option) }} </div>
                <div v-if="isOptionSelected(option)" class="option__icon">
                  <Icon class="sui-icon" name="done"></Icon>
                </div>
              </slot>
            </div>
          </template>
          <template v-if="optionsMark === 'checkbox'">
            <div v-for="(option, _index) in options" :key="_index" class="option-list__element option">
              <slot name="option" :option="option">
                <SCheckbox
                  :label="getLabel(option)" 
                  :name="`${name}-${_index}`" 
                  :model-value="isOptionSelected(option)" 
                  @update:model-value="onOptionCheckboxChange($event, option)"
                >
                </SCheckbox>
              </slot>
            </div>
          </template>
        </div>
        <slot name="afterList" />
      </slot>
      <slot v-else name="empty-options" :options="options">
        <div class="option-list option-list--empty">
          <div>{{ emptyOptionsText }}</div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts" generic="SelectOption extends BaseSelectOption">
  import { toRefs, ref, onMounted, getCurrentInstance, onUnmounted, computed, PropType, defineExpose } from 'vue'

  // Components
  import SCheckbox from '../checkbox/Checkbox.vue'
  import Icon from '../icon/Icon.vue'
  import SChips from '../chips/Chips.vue'

  // Types
  import type { StyleValue, CSSProperties } from 'vue'
  import type { BaseSelectOption } from './select.types'

  const props = defineProps({
    modelValue: {
      type: [Array, Object] as PropType<SelectOption | SelectOption[]>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      dafault: false
    },
    optionKey: {
      type: String,
      default: 'id'
    },
    optionValue: {
      type: String,
      default: 'value'
    },
    optionsMark: {
      type: String,
      default: 'checkbox'
    },
    optionsCountLabel: {
      type: String,
      default: 'Options:'
    },
    options: {
      type: Array<SelectOption>,
      default: () => []
    },
    emptyOptionsText: {
      type: String,
      default: 'Нет доступных опций'
    },
    hasErrors: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean, 
      default: false,
    },
    chips: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    dropDownStyles: {
      type: Object as PropType<CSSProperties>,
      default: () => {}
    }
  })

  const { disabled, readonly, modelValue, label, name, hasErrors, options, optionKey, optionValue, multiple, chips, searchable, clearable, optionsCountLabel, optionsMark, inline, dropDownStyles } = toRefs(props)

  const emit = defineEmits<{
    /**
    * Emit on update modelValue
    */
    'update:modelValue': [option: SelectOption | SelectOption[]],
    /**
    * Emit on select get focus
    */
    'focus': [void],
    /**
    * Emit on input in search string
    */
    'search': [string],
    /**
    * Emit on clear button click
    */
    'clear': [void],
    /**
    * Emit on hide dropdown option list
    */
    'hide': [void],
    /**
    * Emit on visibility changes
    */
    'visibility': [boolean],
    /**
    * Emit on show dropdown option list
    */
    'show': [void],
    /**
    * Emit on scrolled to end in option list
    */
    'options-scrolled': [void]
  }>()

  const optionsVisibility = ref(false)
  const searchString = ref('')
  const currentInstance = getCurrentInstance()
  const searchFocused = ref<boolean>(false)
  const search = ref(null)

  const dropdownIconName = computed(() => {
    return optionsVisibility.value ? 'arrow_m_up' : 'arrow_m_down'
  })

  const isClearableTriggerVisible = computed(() => {
    if(!clearable.value) return false
    if(Array.isArray(modelValue.value)) return modelValue.value.length
    if(typeof modelValue.value === 'object') return Object.keys(modelValue.value).length
    return false
  })

  const searchInputSize = computed(() => {
    return `${searchString.value.length}ch`
  })

  const labelFocused = computed(() => {
    if(multiple.value) {
      return modelValue.value.length || searchFocused.value
    } 
    return modelValue.value || searchFocused.value
  })

  const dropDownVisibility = computed(() => {
    if (inline.value) {
      return true
    } else {
      return optionsVisibility.value
    }
  })

  const dropdownStyles = computed<StyleValue>(() => {
    if(inline.value) {
      return {        
        position: 'relative',
        marginTop: '4px',
        ...dropDownStyles.value,
      }
    }
    return {
      position: 'absolute',
      left: 0,
      top: 'calc(100% + 4px)',
      zIndex: 9999,
      ...dropDownStyles.value,
    }
  })

  function onSearchStringInput(event: Event): void {
    searchString.value = (event.target as HTMLTextAreaElement).value  || ''
    emit('search', searchString.value)
  }

  function onFocusIn() {
    if (search.value && searchable.value) {
      (search?.value as HTMLElement)?.focus?.()
      searchFocused.value =  true
    }
    showDropdown()
  }

  function onClearClick() {
    hideDropdown()
    emit('clear')
  }

  function isOptionSelected(option: SelectOption): boolean {
    if (multiple.value) {
      const _idx = modelValue.value.findIndex((mv_option: SelectOption) => {
        if (optionKey.value) {
          return mv_option?.[optionKey.value] === option?.[optionKey.value]
        }
        return mv_option === option
      })
      return _idx != -1
    } 

    if (optionKey.value) {
      return modelValue.value?.[optionKey.value] === option?.[optionKey.value]
    }
      
    return modelValue.value === option
  }

  function getLabel(option: SelectOption): string {
    return (option?.[optionValue.value] as string) || ''
  }
  
  function onOptionCheckboxChange(event: Event, option: SelectOption): void {
    if(!multiple.value) {
      emit('update:modelValue', option)
      hideDropdown()
    } else {
      if(event) {
        addOption(option)
      } else {
        const idx = findOptionIndex(option)
        removeOption(idx)
      }
    }
  }

  function onOptionClick(option: SelectOption): void {
    if (!multiple.value) {
      emit('update:modelValue', option)
      hideDropdown()
    } else {
      const _idx = modelValue.value.findIndex((el: SelectOption) => {
        if (optionKey?.value) {
          return el?.[optionKey.value] === option?.[optionKey.value]
        }
        return el === option      
      })

      if(_idx === -1) {
        addOption(option)
      } else {
        removeOption(_idx)
      }
    }
  }

  function clickOutside({ target }: Event): void {
    const el = currentInstance?.subTree.el
    if (!(el === target || (el as HTMLElement).contains(target as Node))) {
      if (!disabled.value && !readonly.value) {
        hideDropdown()
      }
    }
  }

  function onDropdownIconClick(): void {
    if (optionsVisibility.value) {
      hideDropdown()
    } else {
      showDropdown()
    }
  }

  function showDropdown(): void {
    optionsVisibility.value = true
    emit('show')
    emit('visibility', optionsVisibility.value)
  }

  function hideDropdown(): void {
    optionsVisibility.value = false

    if (searchString.value) {
      searchString.value = ''
      emit('search', searchString.value)
    }

    searchFocused.value = false
    emit('hide')
    emit('visibility', optionsVisibility.value)
  }

  function findOptionIndex(option: SelectOption): number {
    const _idx = modelValue.value.findIndex((mv_option: SelectOption) => {
      if (optionKey.value) {
        return mv_option?.[optionKey.value] === option?.[optionKey.value]
      } else {
        return mv_option === option
      }
    })
    return _idx
  }

  function addOption(option: SelectOption): void {
    const res: SelectOption[] = [...modelValue.value, option]
    emit('update:modelValue', res)
  }

  function removeOption(idx: number): void {
    const arr = [...modelValue.value]
    arr.splice(idx, 1)
    emit('update:modelValue', arr)
  }

  function onRemoveChipsClick(value: SelectOption): void {
    const idx = findOptionIndex(value)
    removeOption(idx)
  }

  function onOptionsScroll(evt: Event) {
    const el = evt.target as HTMLElement
    if (el.scrollHeight === el.scrollTop + el.clientHeight) {
      emit('options-scrolled')
    }
  }

  onMounted(() => {
    document.body.addEventListener('click', clickOutside)
  })

  onUnmounted(() => {
    document.body.removeEventListener('click', clickOutside)
  })

  defineExpose({
    onOptionClick,
    isOptionSelected,
    getLabel,
    onOptionCheckboxChange
  })
</script>
  
<style scoped lang="scss">
  @import '@/assets/styles/scss/mixins.scss';
  .required-indicator {
    color: #FF0A47
  }
  
  .sui-select {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 52px;
    height: fit-content;
    width: 100%;
    background-color: #fff;

    &__control-wrapper {
      display: flex;
      position: relative;
      padding: 7px 12px 6px 12px;
      border-radius: 4px;
      width: 100%;
      height: 100%;
      border: 1px var(--stroke-select) solid;
    }

    &__options-wrapper { 
      position: relative;     
      width: 100%;
      background-color: #fff;
      overflow: auto;
      border-radius: 4px;
      border: 1px var(--stroke-cards) solid; 
      box-shadow: 0px 10px 10px 0px #0000000D;
      @include scroll;

      // position: absolute;
      // left: 0;
      // top: calc(100% + 4px);
      // z-index: 9999;     
      
    }
  
    &--focused {
      border: 1px var(--color-button-light) solid;
    }
  
    &--disabled {
      pointer-events: none;
      background-color: var(--color-bg-tertiary);
      color: var(--color-text-secondary);
      border-color: var(--stroke-select);
    }
  
    &--readonly {
      pointer-events: none;
      background-color: #FFF;
      color: var(--color-text-tertiary);
      border-color: var(--stroke-select);
    }
    
    &--error {
      border-color: var(--color-button-normal);
    }
  
    &__wrapper {
      height: 100%;
      flex-grow: 1;
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      position: relative;
    }
  
    &__control {
      background-color: transparent;
      color: inherit;
      padding: 0px;
      outline: none;   
      align-self: flex-end;
      
      padding: 8px 12px;
      border: 1px var(--stroke-select) solid;
      border-radius: 4px;
      max-height: 100%;
      height: auto;
  
      &:focus + label, &--has-value + label {
        top: 0px;
        font-size: 12px;
        line-height: 16px;
        transform: translateY(0);
      }

      &--has-errors {
        border-color: var(--color-button-default);
      }

      .sui-icon {
        height: 24px;
        width: 24px;
      }
    }
  
    &__label {
      top: 8px;
      pointer-events: none;
      position: absolute;
      margin-bottom: 0;
      transform-origin: left;
      color: #BABCC2;
      transition: all 0.2s ease-out;
    }
  }

  .option-list {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 4px;
    font-size: 14px;
    line-height: 20px;

    &__element {
      position: relative;
      cursor: pointer;
      
      gap: 8px;
      padding: 10px 16px;
      height: 40px;
      border-radius: 4px;

      &:hover {
        background-color: var(--color-bg-tertiary);
      }

      &--selected {
        background-color: var(--color-bg-quaternary);
      }
    }
  }

  .option {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    box-sizing: content-box;
    flex: 1;

    &__title {
      flex-grow: 1;
      overflow: hidden;
      text-wrap: nowrap;
      text-overflow: ellipsis;
    }

    &__icon {
      height: 20px;
      width: 20px;
      flex-shrink: 0;
    }
    :deep(.sui-checkbox) {
      flex-grow: 1;
    }
    :deep(.sui-checkbox__container) {
      flex-grow: 1;
    }
    :deep(.sui-checkbox__title-container) {
      flex-grow: 1
    }
  }

  .sui-icon {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }

  .chips {
    display: flex;
    overflow: hidden;
    flex-wrap: nowrap;
    align-items: center;
    background-color: var(--color-hint);
    height: 20px;
    padding: 2px 8px;
    color: var(--color-text-contrast);
    border-radius: 4px;
    gap: 8px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    max-width: 100%;

    &__title {
      max-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &__icon {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      cursor: pointer;
    }

    &__search-container {
      flex: 1;
      min-height: 20px;
      max-width: 100%;

      & div {
        display: block;
        outline: none;
        width: 100%;
      }
    }

    &__search {
      height: 100%;
      width: 100%;
      border: none;
    }
  }

  .search-container {
    display: flex;
    align-items: center;
    flex: 1;
    min-height: 20px;
    max-width: 100%;
    font-size: 14px;

    & .search-control {
      display: block;
      border: 0px;
      outline: none;
      min-width: 100%;
      height: 100%;
      font-family: inherit;
    }
  }

  .control {
    position: relative;
    display: flex;
    flex-grow: 1;
    min-height: 100%;
    height: auto;
    width: 100%;
    gap: 8px;
    box-sizing: border-box;

    &__view {
      display: flex;
      flex-direction: column;
      position: relative;
      flex-grow: 1;
      width: 100%;
      overflow: hidden;
    }

    &__right-slot, &__clear-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__label-container {
      height: 16px;
      flex-shrink: 0;
    }

    &__options-container {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      align-items: center;
      width: 100%;
    }

    &__label {
      position: absolute;
      top: 8px;
      color: #BABCC2;
      pointer-events: none;
      transform-origin: left;
      transition: all 0.2s ease-out;
      font-size: 14px;

      &--focused {
        top: 0px;
        font-size: 12px;
        line-height: 16px;
        transform: translateY(0);
      }
    }
  }
</style>
  